import { useState } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { toast } from 'sonner'
import { Eye, EyeOff } from 'lucide-react'

interface LoginFormProps {
  handleLogin: (email: string) => void
  error: string
  loading: boolean
  email: string
  setEmail: (email: string) => void
}

export default function LoginForm({
  handleLogin,
  error,
  loading,
  email,
  setEmail
}: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false)
  const [password, setPassword] = useState('')
  const [_loginMethod, setLoginMethod] = useState('password')

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !password) return

    try {
      const response = await fetch('https://backend.testeswaffle.org/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Credenciais inválidas')
      }

      const userData = {
        email,
        isAuthenticated: true,
        token: data.token,
        role: data.role,
        permissions: data.permissions
      }

      localStorage.setItem('user', JSON.stringify(userData))
      window.location.reload()
    } catch (err: any) {
      toast.error('Erro ao fazer login', {
        description: err.message || 'Credenciais inválidas. Por favor, tente novamente.',
        duration: 5000,
        position: 'top-right',
      })
    }
  }

  // const handleBrandLogin = async (e: React.FormEvent) => {
  //   e.preventDefault()
    
  //   if (!email || !password) return

  //   try {
  //     const response = await fetch('https://backend.testeswaffle.org/auth/loginBrand', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json'
  //       },
  //       body: JSON.stringify({ email, password })
  //     })

  //     const data = await response.json()

  //     if (!response.ok) {
  //       throw new Error(data.message || 'Credenciais inválidas')
  //     }

  //     const userData = {
  //       email,
  //       isAuthenticated: true,
  //       token: data.token,
  //       role: data.role,
  //       brand_name: data.brand_name,
  //       permissions: data.permissions
  //     }

  //     localStorage.setItem('user', JSON.stringify(userData))
  //     window.location.reload()
  //   } catch (err: any) {
  //     toast.error('Erro ao fazer login', {
  //       description: err.message || 'Credenciais inválidas. Por favor, tente novamente.',
  //       duration: 5000,
  //       position: 'top-right',
  //     })
  //   }
  // }

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    handleLogin(email)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="flex w-full max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Lado esquerdo - Imagem */}
        <div className="hidden md:flex w-1/2 bg-gray-100 dark:bg-gray-700 items-center justify-center p-8">
          <div className="w-full h-full flex items-center justify-center">
            <img
              src="/favicon.png"
              alt="Logo"
              className="w-48 h-48 object-contain"
            />
          </div>
        </div>

        {/* Lado direito - Formulário */}
        <div className="w-full md:w-1/2 p-8">
          <div className="w-full max-w-sm mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Bem-vindo(a) de volta
              </h1>
              <p className="text-gray-500 dark:text-gray-400">
                Selecione uma das opções abaixo para continuar
              </p>
            </div>

            <Tabs defaultValue="password" className="w-full" onValueChange={setLoginMethod}>
              <TabsList className="grid w-full grid-cols-3 mb-8">
                <TabsTrigger value="password">Senha</TabsTrigger>
                <TabsTrigger value="magic">Magic Link</TabsTrigger>
              </TabsList>

              <TabsContent value="password">
                <Card>
                  <CardHeader>
                    <CardTitle>Login com Senha</CardTitle>
                    <CardDescription>
                      Entre com seu email e senha
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handlePasswordLogin} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          placeholder="seu@email.com"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="password">Senha</Label>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2"
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4 text-gray-500" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-500" />
                            )}
                          </button>
                        </div>
                      </div>
                      <Button type="submit" className="w-full">
                        Entrar
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="magic">
                <Card>
                  <CardHeader>
                    <CardTitle>Magic Link</CardTitle>
                    <CardDescription>
                      Receba um link de acesso no seu email
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleMagicLink} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="magic-email">Email</Label>
                        <Input
                          id="magic-email"
                          placeholder="seu@email.com"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      <Button type="submit" className="w-full" disabled={loading}>
                        {loading ? "Enviando..." : "Enviar Link de Acesso"}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {error && (
              <div className="mt-4 p-3 text-sm text-red-500 bg-red-50 dark:bg-red-900/50 dark:text-red-200 rounded-md">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
